import { forwardRef, type HTMLAttributes, type ReactNode } from "react";
import { RefreshCw } from "lucide-react";
import { IconButton } from "./IconButton";
import { cn } from "@/lib/cn";

// Stage 5: local replacement for @datasirpi/ui's <PageHeader>. Layout and
// CSS ported letter-for-letter from the vendor's compiled bundle and its
// PageHeader CSS — new .rc-page-header class names. Uses the local
// ./Typography sibling internally, same as the vendor used its own
// <Typography>.
//
// Naming note: `src/components/ui/PageHeader.tsx` was a separate, unrelated
// local component with a different API. It had no importers at all by the
// time Phase 4 reached it, so it is deleted rather than migrated — the
// naming hazard this comment used to warn about is gone with it.
//
// ── Phase 4.7: the slots a console page header actually needs ───────────
// Every page in this app was rebuilding the same row by hand — title, a
// subtitle, a refresh button, sometimes a time filter — which is why H1
// size and placement varied from page to page (finding F17) and why only
// 5 of 99 routes used this component at all. The reason they did not is
// that it had nowhere to put the two controls a monitoring page always
// carries. It does now:
//
//   timeRange  a <TimeRangePicker>, placed before the actions
//   onRefresh  a standard refresh control, so "refresh" is one affordance
//              in one position instead of a bespoke button per page
//
// `refreshing` drives the spin AND disables the control, which most of the
// hand-rolled versions forgot — several let you queue five refreshes.
//
// One vendor class NOT ported: `ds-page-header--small` was applied to the
// root element when `size="sm"`, but has zero CSS rules anywhere in the
// vendor's own stylesheet — confirmed by grepping its full compiled CSS.
// The actual size change comes entirely from swapping the heading tag
// (h1→h3) via Typography's `variant`/`as`, which this still does
// identically. Dropping the dead class changes nothing observable.
export interface PageHeaderProps extends Omit<HTMLAttributes<HTMLElement>, "title"> {
  title: ReactNode;
  badge?: ReactNode;
  description?: ReactNode;
  breadcrumbs?: ReactNode;
  primaryAction?: ReactNode;
  secondaryActions?: ReactNode;
  size?: "default" | "sm";
  /** A <TimeRangePicker>. Sits ahead of the actions, where filters belong. */
  timeRange?: ReactNode;
  /** Renders the standard refresh control. */
  onRefresh?: () => void;
  /** Spins the icon and disables the control while a refresh is in flight. */
  refreshing?: boolean;
  /** Leading glyph. Decorative — the title carries the meaning. */
  icon?: ReactNode;
}

export const PageHeader = forwardRef<HTMLElement, PageHeaderProps>(function PageHeader(
  { title, badge, description, breadcrumbs, primaryAction, secondaryActions, size = "default",
    timeRange, onRefresh, refreshing, icon, className, ...rest },
  ref
) {
  const isSmall = size === "sm";
  const Heading = isSmall ? "h2" : "h1";
  return (
    <header ref={ref} className={cn("rc-page-header", className)} data-size={size} {...rest}>
      {breadcrumbs}
      <div className="rc-page-header__row">
        <div className="rc-page-header__title-block">
          <div className="rc-page-header__title-row">
            {icon ? <span className="rc-page-header__icon" aria-hidden>{icon}</span> : null}
            {/* A plain heading element, not <Typography variant="h1">.
                Typography's h1 step is 32px — correct for a document, too
                loud for a console page title sitting above a toolbar, and
                it is what made the page titles in this app disagree: the
                dashboards used their own 24px masthead while every other
                route rendered a 32px h1. The size now lives in
                .rc-page-header__title so there is exactly one value, and
                the element stays a real <h1> for the document outline. */}
            <Heading className="rc-page-header__title">{title}</Heading>
            {badge ? <span className="rc-page-header__badge">{badge}</span> : null}
          </div>
          {description ? (
            <p className="rc-page-header__description">{description}</p>
          ) : null}
        </div>
        {primaryAction || secondaryActions || timeRange || onRefresh ? (
          <div className="rc-page-header__actions">
            {timeRange}
            {secondaryActions}
            {onRefresh ? (
              <IconButton
                aria-label="Refresh"
                tooltip={refreshing ? "Refreshing…" : "Refresh"}
                size="sm"
                onClick={onRefresh}
                disabled={refreshing}
                icon={<RefreshCw size={15} className={refreshing ? "rc-page-header__spin" : undefined} />}
              />
            ) : null}
            {primaryAction}
          </div>
        ) : null}
      </div>
    </header>
  );
});
